import React from 'react';
import { NavLink } from 'react-router-dom';
import { 
  Home, 
  Shield, 
  FileText, 
  Activity, 
  BookOpen, 
  AlertTriangle, 
  Users,
  Settings,
  BarChart3,
  Folder,
  List
} from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import { cn } from '../../lib/utils';

interface NavItem {
  name: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  requiredRole?: 'admin' | 'auditor' | 'user';
}

const navigation: NavItem[] = [
  { name: 'Dashboard', href: '/dashboard', icon: Home },
  { name: 'Compliance', href: '/compliance', icon: Shield },
  { name: 'Documents', href: '/documents', icon: FileText },
  { name: 'Assets', href: '/assets', icon: Activity },
  { name: 'Training', href: '/training', icon: BookOpen },
  { name: 'Incidents', href: '/incidents', icon: AlertTriangle },
  { name: 'Code Lists', href: '/codelists', icon: List },
  { name: 'Reports', href: '/reports', icon: BarChart3 },
  { name: 'File Manager', href: '/files', icon: Folder },
  { name: 'Users', href: '/users', icon: Users, requiredRole: 'admin' },
  { name: 'Settings', href: '/settings', icon: Settings },
];

export function Sidebar() {
  const { user } = useAuthStore();

  const canAccessItem = (item: NavItem) => {
    if (!item.requiredRole) return true;
    if (!user) return false;

    const roleHierarchy = {
      user: 0,
      auditor: 1,
      admin: 2
    };

    const userRoleLevel = roleHierarchy[user.role];
    const requiredRoleLevel = roleHierarchy[item.requiredRole];

    return userRoleLevel >= requiredRoleLevel;
  };

  const filteredNavigation = navigation.filter(canAccessItem);

  return (
    <div className="flex w-16 lg:w-64 flex-col fixed inset-y-0 top-16">
      <div className="flex flex-col flex-grow pt-5 bg-white overflow-y-auto border-r border-gray-200">
        <div className="flex flex-col flex-grow">
          <nav className="flex-1 px-2 pb-4 space-y-1">
            {filteredNavigation.map((item) => {
              const IconComponent = item.icon;
              return (
                <NavLink
                  key={item.name}
                  to={item.href}
                  className={({ isActive }) =>
                    cn(
                      'group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors',
                      isActive
                        ? 'bg-blue-50 text-blue-700 border-r-2 border-blue-700'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    )
                  }
                  title={item.name}
                >
                  {({ isActive }) => (
                    <>
                      <IconComponent
                        className={cn(
                          'lg:mr-3 flex-shrink-0 h-5 w-5',
                          isActive ? 'text-blue-500' : 'text-gray-400 group-hover:text-gray-500'
                        )}
                      />
                      <span className="hidden lg:inline">{item.name}</span>
                      {item.requiredRole === 'admin' && (
                        <span className="ml-auto hidden lg:inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                          Admin
                        </span>
                      )}
                    </>
                  )}
                </NavLink>
              );
            })}
          </nav>
        </div>

        {/* User Info Footer */}
        <div className="flex-shrink-0 p-4 border-t border-gray-200">
          <div className="flex items-center justify-center lg:justify-start">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                <span className="text-sm font-medium text-white">
                  {user?.firstName[0]}{user?.lastName[0]}
                </span>
              </div>
            </div>
            <div className="ml-3 hidden lg:block">
              <p className="text-sm font-medium text-gray-700">
                {user?.firstName} {user?.lastName}
              </p>
              <p className="text-xs text-gray-500">
                {user?.role ? user.role.charAt(0).toUpperCase() + user.role.slice(1) : 'User'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
